import React, { useState } from 'react';
import { ProductReview, ShopReview } from '../../domain/types';
import { StarRating } from './StarRating';
import { logger } from '../../infrastructure/logger';
import { formatRelativeTimeFull } from '../../utils/formatters';

interface ReviewCardProps {
  review: ProductReview | ShopReview;
  showShopLink?: boolean;
  shopSlug?: string;
  onHelpful?: (reviewId: string) => Promise<void>;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  showShopLink = false,
  shopSlug,
  onHelpful
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHelpfulClicked, setIsHelpfulClicked] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpful || 0);
  const [isLoading, setIsLoading] = useState(false);

  const hasImages = 'images' in review && review.images && review.images.length > 0;
  const hasResponse = review.response && review.response.length > 0;
  const shouldTruncate = !isExpanded && review.comment.length > 150;

  const handleHelpful = async () => {
    if (isHelpfulClicked || isLoading || !onHelpful) return;

    setIsLoading(true);
    try {
      await onHelpful(review.id);
      setIsHelpfulClicked(true);
      setHelpfulCount(prev => prev + 1);
    } catch (error) {
      logger.error('Error marking review as helpful', error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="bg-white dark:bg-surface-dark rounded-xl p-4 border border-gray-200 dark:border-white/10 shadow-sm">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shrink-0">
          <span className="text-white text-sm font-bold">
            {getInitials(review.customerName)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-text-main dark:text-white text-sm">
                {review.customerName}
              </span>
              {hasResponse && (
                <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
                  Con respuesta
                </span>
              )}
              {review.status === 'pending' && (
                <span className="text-[10px] bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full font-medium">
                  En revisión
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500 shrink-0">
              {formatRelativeTimeFull(review.createdAt)}
            </span>
          </div>

          {/* Rating */}
          <StarRating rating={review.rating} size="sm" className="mb-2" />

          {/* Comment */}
          {review.comment && (
            <p className="text-sm text-text-secondary dark:text-gray-300 leading-relaxed">
              {shouldTruncate ? review.comment.slice(0, 150) + '...' : review.comment}
              {review.comment.length > 150 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-primary text-xs font-medium ml-2 hover:underline focus:outline-none"
                >
                  {isExpanded ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </p>
          )}

          {/* Images */}
          {hasImages && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
              {(review as ProductReview).images!.slice(0, 3).map((imageUrl, idx) => (
                <img
                  key={idx}
                  src={imageUrl}
                  alt={`Foto de la reseña ${idx + 1}`}
                  className="w-20 h-20 rounded-lg object-cover shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                />
              ))}
              {(review as ProductReview).images!.length > 3 && (
                <div className="w-20 h-20 rounded-lg bg-gray-200 dark:bg-white/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    +{(review as ProductReview).images!.length - 3}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Partner Response */}
          {hasResponse && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs font-bold text-blue-700 dark:text-blue-400 mb-1">
                Respuesta del vendedor
              </p>
              <p className="text-sm text-text-secondary dark:text-gray-300">
                {review.response}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-white/5">
            <button
              onClick={handleHelpful}
              disabled={isHelpfulClicked || isLoading || !onHelpful}
              className={`flex items-center gap-1.5 text-xs transition-colors ${
                isHelpfulClicked
                  ? 'text-primary font-bold'
                  : 'text-gray-500 hover:text-primary'
              } disabled:cursor-not-allowed`}
            >
              <span className={`material-symbols-outlined text-sm ${isHelpfulClicked ? 'filled' : ''}`}>
                thumb_up
              </span>
              <span>{helpfulCount} útil</span>
            </button>

            {showShopLink && shopSlug && (
              <a
                href={`/tienda/${shopSlug}`}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-primary transition-colors"
              >
                <span className="material-symbols-outlined text-sm">storefront</span>
                <span>Ver tienda</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
